import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase } from '../../../supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CategoryForm = ({ onSuccess, isModal = false, initialData = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Déterminer si nous sommes en mode édition soit par l'URL, soit par les données initiales
  const isEditMode = !!id || (initialData && initialData.id);

  const [formData, setFormData] = useState(initialData || {
    name: '',
    description: '',
    service_id: '',
    parent_id: null
  });

  // Définition de la toolbar et des formats pour ReactQuill
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 
    'italic', 
    'underline', 
    'strike',
    'list', 
    'bullet',
    'link'
  ];

  // Gestion du changement de la description (ReactQuill)
  const handleDescriptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
  };

  const [loading, setLoading] = useState(isEditMode);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const supabase = getSupabase();
      
      try {
        // Charger les services disponibles
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('id, name');

        if (servicesError) {
          setError('Erreur lors du chargement des services');
          return;
        }

        setServices(servicesData || []);

        // Si en mode édition via URL (et non via modal avec initialData)
        // Nous ne chargeons les données que si nous n'avons pas déjà des données initiales
        if (isEditMode && id && !initialData) {
          const { data: categoryData, error: categoryError } = await supabase
            .from('categories')
            .select('*')
            .eq('id', id)
            .single();

          if (categoryError) {
            setError('Erreur lors du chargement de la catégorie');
            return;
          }

          if (categoryData) {
            setFormData(categoryData);
          }
        }
        
        // Si nous avons des données initiales (via modal), on les utilise directement
        // Pas besoin de faire une requête supplémentaire

        // Fetch categories (exclusion de la catégorie actuelle si en mode édition)
        const categoriesQuery = supabase
          .from('categories')
          .select('id, name, parent_id');

        // Déterminer l'ID à exclure (soit de l'URL, soit des données initiales)
        const categoryId = id || (initialData && initialData.id);
        
        if (isEditMode && categoryId) {
          categoriesQuery.neq('id', categoryId);
        }

        const { data: categoriesData, error: categoriesError } = await categoriesQuery;

        if (categoriesError) {
          setError('Erreur lors du chargement des catégories');
          return;
        }

        setCategories(categoriesData || []);
        setLoading(false);
      } catch (err) {
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };

    fetchCategories();
  }, [id, isEditMode, initialData]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
    if (success) {
      toast.success(success);
      
      // Appeler le callback onSuccess si fourni
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [error, success, onSuccess]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validation
      if (!formData.name) {
        setError('Le nom de la catégorie est requis');
        return;
      }

      const supabase = getSupabase();
      
      // Déterminer l'ID à utiliser pour la mise à jour (soit de l'URL, soit des données initiales)
      const categoryId = id || (initialData && initialData.id);
      
      const { data, error } = isEditMode
        ? await supabase
            .from('categories')
            .update(formData)
            .eq('id', categoryId)
            .select() 
        : await supabase
            .from('categories')
            .insert(formData)
            .select(); 

      if (error) {
        setError(error.message || 'Erreur lors de la sauvegarde de la catégorie');
        return;
      }

      const updatedCategory = data?.[0] || null;
      
      const successMessage = isEditMode 
        ? 'Catégorie mise à jour avec succès' 
        : 'Catégorie créée avec succès';
      
      setSuccess(successMessage);

      console.log('Catégorie traitée:', updatedCategory);

      if (!isModal) {
        navigate('/admin/categories');
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          {success}
        </div>
      )}
      <Toaster position="top-right" />
      <div>
        <form onSubmit={handleSubmit} className="bg-white rounded-lg p-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Nom de la catégorie *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="tracking-input w-full form-input"
                value={formData.name}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="service_id">
                Service associé
              </label>
              <select
                id="service_id"
                name="service_id"
                className="tracking-input w-full form-input"
                value={formData.service_id}
                onChange={handleChange}
              >
                <option value="">Sélectionner un service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="parent_id">
                Catégorie parente
              </label>
              <select
                id="parent_id"
                name="parent_id"
                className="tracking-input w-full form-input"
                value={formData.parent_id}
                onChange={handleChange}
              >
                <option value={null}>Aucune catégorie parente</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                Description
              </label>
              <ReactQuill
                id="description"
                theme="snow"
                value={formData.description}
                onChange={handleDescriptionChange}
                modules={modules}
                formats={formats}
                placeholder="Décrivez brièvement la catégorie"
                className="bg-white"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-6 space-x-4">
            <button
              type="button"
              onClick={() => isModal ? onSuccess?.() : navigate('/admin/categories')}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-modern btn-primary-modern"
            >
              {isEditMode ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CategoryForm.propTypes = {
  onSuccess: PropTypes.func,
  isModal: PropTypes.bool,
  initialData: PropTypes.object
};

export default CategoryForm;
